'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import Image from 'next/image';

const serviceLinks = [
  { label: 'Car-O-Liner Chassis Alignment', href: '/services/chassis-alignment' },
  { label: 'Airbag & SRS Repair', href: '/services/airbag-srs' },
  { label: 'Module Coding (Mercedes/BMW)', href: '/services/module-coding' },
  { label: 'Body Repairs', href: '/services/body-repairs' },
];

const navLinks = [
  { label: 'Home', href: '/' },
  { label: 'How Non-Fault Claims Work', href: '/how-non-fault-claims-work' },
  { label: 'Services', href: '#', hasDropdown: true },
  { label: 'Gallery', href: '/gallery' },
  { label: 'FAQ', href: '/faq' },
  { label: 'About Us', href: '/about' },
  { label: 'Contact', href: '/contact' },
];

export default function Header() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hamburgerRef = useRef<HTMLButtonElement>(null);
  const mobileMenuRef = useRef<HTMLDivElement>(null);
  const servicesTriggerRef = useRef<HTMLButtonElement>(null);
  const dropdownItemRefs = useRef<(HTMLAnchorElement | null)[]>([]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [menuOpen]);

  // Return focus to hamburger when mobile menu closes
  useEffect(() => {
    if (!menuOpen && hamburgerRef.current) {
      hamburgerRef.current.focus();
    }
  }, [menuOpen]);

  // Focus trap for mobile menu
  useEffect(() => {
    if (!menuOpen) return;
    const menu = mobileMenuRef.current;
    if (!menu) return;
    const focusableSelectors = 'a[href], button:not([disabled]), input, textarea, select, [tabindex]:not([tabIndex="-1"])';
    const getFocusable = () => Array.from(menu.querySelectorAll<HTMLElement>(focusableSelectors));
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;
      const focusable = getFocusable();
      if (focusable.length === 0) return;
      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) {
          e.preventDefault();
          last.focus();
        }
      } else {
        if (document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    menu.addEventListener('keydown', handleKeyDown);
    // Focus first element in menu
    const focusable = getFocusable();
    if (focusable.length > 0) focusable[0].focus();
    return () => menu.removeEventListener('keydown', handleKeyDown);
  }, [menuOpen]);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard handler for Services dropdown
  const handleServicesKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      setServicesOpen((prev) => !prev);
    } else if (e.key === 'Escape') {
      setServicesOpen(false);
      servicesTriggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setServicesOpen(true);
      setTimeout(() => dropdownItemRefs.current[0]?.focus(), 50);
    }
  }, []);

  const handleDropdownItemKeyDown = useCallback((e: React.KeyboardEvent, index: number) => {
    if (e.key === 'Escape') {
      e.preventDefault();
      setServicesOpen(false);
      servicesTriggerRef.current?.focus();
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      const next = dropdownItemRefs.current[index + 1];
      if (next) next.focus();
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      if (index === 0) {
        servicesTriggerRef.current?.focus();
      } else {
        const prev = dropdownItemRefs.current[index - 1];
        if (prev) prev.focus();
      }
    }
  }, []);

  return (
    <>
      {/* Skip to main content */}
      <a href="#main-content" className="skip-to-content">
        Skip to main content
      </a>

      {/* Mobile overlay menu */}
      <div
        ref={mobileMenuRef}
        role="dialog"
        aria-modal="true"
        aria-label="Navigation menu"
        className={`fixed inset-0 z-[60] bg-[#0a0a0a] flex flex-col overflow-y-auto md:hidden transition-all duration-300 ${
          menuOpen ? 'opacity-100 pointer-events-auto' : 'opacity-0 pointer-events-none'
        }`}
      >
        <div className="flex items-center justify-between px-6 py-5 border-b border-white/[0.08]">
          <Link href="/" onClick={() => setMenuOpen(false)} className="flex items-center gap-3">
            <Image
              src="/assets/images/logo_black_charcoal-1773255396754.png"
              alt="No Limit Auto Body Specialist logo"
              height={40}
              width={120}
              className="object-contain"
              style={{ height: '40px', width: 'auto' }}
              priority
            />
          </Link>
          <button
            onClick={() => setMenuOpen(false)}
            className="text-white/60 hover:text-white transition-colors p-3 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Close navigation menu"
          >
            <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M18 6L6 18M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <nav aria-label="Mobile navigation" className="flex flex-col px-6 py-8 gap-1">
          {navLinks.map((link) => {
            if (link.hasDropdown) {
              return (
                <div key={link.label}>
                  <button
                    onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
                    className="w-full flex items-center justify-between py-4 text-left font-fraunces font-light text-xl text-white/70 hover:text-white transition-colors duration-200 border-b border-white/[0.06] min-h-[48px]"
                    aria-expanded={mobileServicesOpen}
                    aria-controls="mobile-services-submenu"
                  >
                    <span>Services</span>
                    <svg
                      width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5"
                      className={`transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}
                      aria-hidden="true"
                    >
                      <path d="M6 9l6 6 6-6"/>
                    </svg>
                  </button>
                  <div
                    id="mobile-services-submenu"
                    className={`pl-4 flex flex-col gap-1 py-2 ${mobileServicesOpen ? '' : 'hidden'}`}
                  >
                    {serviceLinks.map((s) => (
                      <Link
                        key={s.label}
                        href={s.href}
                        onClick={() => setMenuOpen(false)}
                        className="py-3 font-dm-sans text-[15px] text-white/55 hover:text-[#CC1A1A] transition-colors border-b border-white/[0.04] min-h-[48px] flex items-center"
                      >
                        {s.label}
                      </Link>
                    ))}
                  </div>
                </div>
              );
            }
            return (
              <Link
                key={link.label}
                href={link.href}
                onClick={() => setMenuOpen(false)}
                className="py-4 font-fraunces font-light text-xl text-white/70 hover:text-white transition-colors duration-200 border-b border-white/[0.06] min-h-[48px] flex items-center"
              >
                {link.label}
              </Link>
            );
          })}

          <div className="mt-6 flex flex-col gap-3">
            <a
              href="tel:01614001687"
              onClick={() => setMenuOpen(false)}
              className="flex items-center gap-2 font-dm-sans text-[15px] text-white/50 hover:text-white transition-colors min-h-[44px]"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
              0161 400 1687
            </a>
            <Link
              href="/contact"
              onClick={() => setMenuOpen(false)}
              className="mt-2 inline-flex items-center justify-center px-8 py-3 bg-[#CC1A1A] text-white font-dm-sans font-bold text-sm uppercase tracking-widest hover:bg-[#E02020] transition-all min-h-[48px]"
            >
              FREE CLAIM ENQUIRY →
            </Link>
          </div>
        </nav>
      </div>

      {/* Sticky header */}
      <header
        className={`fixed top-0 w-full z-50 transition-all duration-300 ${
          scrolled
            ? 'bg-[#121212] backdrop-blur-md border-b border-white/[0.08] py-3'
            : 'bg-[#121212] border-b border-white/[0.08] py-4'
        }`}
      >
        <div className="max-w-8xl mx-auto px-6 flex items-center justify-between gap-6">
          {/* Logo */}
          <Link href="/" className="flex items-center gap-3 group shrink-0" aria-label="No Limit Auto Body Specialist home">
            <Image
              src="/assets/images/logo_black_charcoal-1773255396754.png"
              alt="No Limit Auto Body Specialist logo"
              height={56}
              width={168}
              className="object-contain"
              style={{ height: '56px', width: 'auto' }}
              priority
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden lg:flex items-center gap-1 xl:gap-2" aria-label="Main navigation">
            {navLinks.map((link) => {
              if (link.hasDropdown) {
                return (
                  <div key={link.label} className="relative" ref={dropdownRef}>
                    <button
                      ref={servicesTriggerRef}
                      onClick={() => setServicesOpen(!servicesOpen)}
                      onMouseEnter={() => setServicesOpen(true)}
                      onKeyDown={handleServicesKeyDown}
                      className="flex items-center gap-1 px-3 py-2 font-dm-sans text-[13px] font-bold text-white hover:text-[#CC1A1A] transition-colors duration-200 tracking-wide min-h-[44px]"
                      aria-expanded={servicesOpen}
                      aria-haspopup="true"
                      aria-controls="services-dropdown"
                    >
                      Services
                      <svg
                        width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"
                        className={`transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}
                        aria-hidden="true"
                      >
                        <path d="M6 9l6 6 6-6"/>
                      </svg>
                    </button>

                    {/* Dropdown */}
                    <div
                      id="services-dropdown"
                      role="menu"
                      onMouseLeave={() => setServicesOpen(false)}
                      className={`absolute top-full left-0 mt-1 w-64 bg-[#121212] border border-white/[0.08] shadow-2xl transition-all duration-200 ${
                        servicesOpen ? 'opacity-100 translate-y-0 pointer-events-auto' : 'opacity-0 -translate-y-2 pointer-events-none'
                      }`}
                    >
                      {serviceLinks.map((s, index) => (
                        <Link
                          key={s.label}
                          href={s.href}
                          ref={(el) => { dropdownItemRefs.current[index] = el; }}
                          role="menuitem"
                          onClick={() => setServicesOpen(false)}
                          onKeyDown={(e) => handleDropdownItemKeyDown(e, index)}
                          className="block px-5 py-3.5 font-dm-sans text-[13px] text-white hover:text-[#CC1A1A] hover:bg-[#CC1A1A]/10 hover:border-l-2 hover:border-[#CC1A1A] transition-all duration-150 border-l-2 border-transparent min-h-[44px] flex items-center"
                        >
                          {s.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                );
              }
              return (
                <Link
                  key={link.label}
                  href={link.href}
                  className="px-3 py-2 font-dm-sans text-[13px] font-bold text-white hover:text-[#CC1A1A] transition-colors duration-200 tracking-wide min-h-[44px] flex items-center"
                >
                  {link.label}
                </Link>
              );
            })}
          </nav>

          {/* Desktop CTA */}
          <div className="hidden lg:flex items-center gap-4 shrink-0">
            <a
              href="tel:01614001687"
              className="flex items-center gap-2 font-dm-sans text-[13px] font-bold text-white hover:text-[#CC1A1A] transition-colors min-h-[44px]"
            >
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" aria-hidden="true">
                <path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07A19.5 19.5 0 013.07 9.81 19.79 19.79 0 01.07 1.18 2 2 0 012.03 0h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L6.09 7.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z"/>
              </svg>
              0161 400 1687
            </a>
            <Link
              href="/contact"
              className="px-5 py-2.5 bg-[#C00000] text-white font-dm-sans font-bold text-[12px] uppercase tracking-[0.15em] hover:bg-[#E00000] transition-all duration-200 min-h-[44px] flex items-center rounded-sm"
            >
              FREE CLAIM
            </Link>
          </div>

          {/* Mobile hamburger */}
          <button
            ref={hamburgerRef}
            onClick={() => setMenuOpen(true)}
            className="lg:hidden text-white p-3 min-w-[44px] min-h-[44px] flex items-center justify-center"
            aria-label="Open navigation menu"
            aria-expanded={menuOpen}
            aria-controls="mobile-menu"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M3 12h18M3 6h18M3 18h18"/>
            </svg>
          </button>
        </div>
      </header>
    </>
  );
}